<template>
  <span v-if="props.anchor" :id="props.anchor" class="anchor"></span>
  <div class="card" v-bind="$attrs">
    <div class="card-header d-flex justify-content-between bg-primary">
      <h4 class="text-uppercase mb-0">Transports</h4>
      <button class="btn btn-link link-dark p-0 link-underline-opacity-0 link-underline-opacity-100-hover" @click="addTransportation()"><i class="fa-solid fa-plus pe-1"></i>Ajouter un transport</button>
    </div>
    <div class="card-body">
      <div v-if="transportations.length === 0" class="text-muted">
        Aucun transport
      </div>
      <div v-else v-for="(transportation,idx) in transportations" :key="transportation.key" class="card mb-3">
        <span :id="`transportations[${idx}]`" class="anchor"></span>
        <div class="card-header d-flex justify-content-between bg-primary bg-opacity-25">
          <h4 class="text-uppercase mb-0">{{transportation.value?.name.length > 0 ? transportation.value?.name : `Transport ${idx + 1}`}}</h4>
          <button class="btn btn-link link-dark p-0" @click="removeTransportation(idx)"><i class="fa-regular fa-trash-can fa-lg"></i></button>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-12 col-md-2">
              <SelectInputComponent
                :value-key="`transportations[${idx}].type`"
                label="Type"
                placeholder="Type"
                :required="true"
                class="mb-3"
                :options="Object.values(TravelDbTransportationTypeEnum).map(value => ({ label: GetTransportationTypeLabel(value), value }))"
              />
            </div>
            <div class="col-12 col-md-8">
              <TextInputComponent
                :value-key="`transportations[${idx}].name`"
                label="Nom groupe"
                placeholder="Nom groupe"
                :required="true"
                class="mb-3"
              />
            </div>
            <div class="col-12 col-md-2">
              <NumberInputComponent
                :value-key="`transportations[${idx}].extraDays`"
                label="Extra Days"
                placeholder="Extra Days"
                :required="true"
                class="mb-3"
              />
            </div>
          </div>

          <AdminTravelEditTransportOption
            class="mb-3"
            :value-key="`transportations[${idx}].options`"
            v-model:validation-schema="optionsValidationSchema"
          />

          <div class="fw-semibold">Aller</div>
          <div class="row mb-3">
            <div class="col-12 col-md-4">
              <TextInputComponent
                :value-key="`transportations[${idx}].outboundCompany`"
                label="Compagnie"
                placeholder="Compagnie"
                :required="true"
                class="mb-3 mb-md-0"
              />
            </div>
            <div class="col-12 col-md-4">
              <TextInputComponent
                :value-key="`transportations[${idx}].outboundItinerary`"
                label="Itinéraire"
                placeholder="Itinéraire"
                :required="true"
                class="mb-3 mb-md-0"
              />
            </div>
            <div class="col-12 col-md-4">
              <TextInputComponent
                :value-key="`transportations[${idx}].outboundDate`"
                label="Date et horaire"
                placeholder="Date et horaire"
                :required="true"
                class="mb-3 mb-md-0"
              />
            </div>
          </div>

          <div class="fw-semibold">Retour</div>
          <div class="row">
            <div class="col-12 col-md-4">
              <TextInputComponent
                :value-key="`transportations[${idx}].returnCompany`"
                label="Compagnie"
                placeholder="Compagnie"
                :required="true"
                class="mb-3 mb-md-0"
              />
            </div>
            <div class="col-12 col-md-4">
              <TextInputComponent
                :value-key="`transportations[${idx}].returnItinerary`"
                label="Itinéraire"
                placeholder="Itinéraire"
                :required="true"
                class="mb-3 mb-md-0"
              />
            </div>
            <div class="col-12 col-md-4">
              <TextInputComponent
                :value-key="`transportations[${idx}].returnDate`"
                label="Date et horaire"
                placeholder="Date et horaire"
                :required="true"
                class="mb-3 mb-md-0"
              />
            </div>
          </div>

        </div>

      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue';
import { useFieldArray } from 'vee-validate';
import {IdNotDefined,ScrollToId } from '@/compositions/helper'
import TextInputComponent from '@/components/common/TextInputComponent.vue';
import SelectInputComponent from '@/components/common/SelectInputComponent.vue';
import AdminTravelEditTransportOption from '@/views/admin/travel/AdminTravelEditTransportOption.vue';
import { GetTransportationTypeLabel } from '@/compositions/helper';
import * as zod from 'zod';

const validationSchema = defineModel<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>('validation-schema',{ required: false, default: () => ({ shape: {} }) });
const optionsValidationSchema = ref<{ shape: zod.ZodRawShape, anchors?: Record<string, string | undefined> }>({ shape: {} });

const props = defineProps<{
  anchor?: string
}>();

const { remove: removeTransportation, push: pushTransportation, fields: transportations } = useFieldArray<TravelDbTransportation>(() => 'transportations');

const addTransportation = () => {
  pushTransportation(<TravelDbTransportation>{
    id: IdNotDefined,
    type: TravelDbTransportationTypeEnum.Vol,
    name: `Transport ${(transportations?.value.length ?? 0) + 1}`,
    extraDays: 0,
    outboundCompany: '',
    outboundItinerary: '',
    outboundDate: '',
    returnCompany: '',
    returnItinerary: '',
    returnDate: ''
  });

  ScrollToId(`transportations[${(transportations?.value.length ?? 1) - 1}]`)
}

const mergedSchema = computed(() => {
  return {
    shape: {
      transportations: zod.array(zod.object({
        id: zod.number().nonnegative(),
        type: zod.nativeEnum(TravelDbTransportationTypeEnum),
        name: zod.string().min(1, 'Le nom du groupe du transport est obligatoire'),
        extraDays: zod.number({ message: 'Jours supplémentaires non valide' }).min(0, 'Les jours supplémentaires doivent être au minimum 0'),
        outboundCompany: zod.string().min(1, 'La compagnie aller est obligatoire').optional(),
        outboundItinerary: zod.string().min(1, 'L\'itinéraire aller est obligatoire').optional(),
        outboundDate: zod.string().min(1, 'La date aller est obligatoire').optional(),
        returnCompany: zod.string().min(1, 'La compagnie retour est obligatoire').optional(),
        returnItinerary: zod.string().min(1, 'L\'itinéraire retour est obligatoire').optional(),
        returnDate: zod.string().min(1, 'La date retour est obligatoire').optional(),
        ...zod.object(optionsValidationSchema.value.shape).shape,
      })).optional()
    },
    anchors: {
      "transportations": props.anchor,
      "transportations[\\d].id": "self",
      "transportations[\\d].type": "self",
      "transportations[\\d].name": "self",
      "transportations[\\d].extraDays": "self",
      "transportations[\\d].outboundCompany": "self",
      "transportations[\\d].outboundItinerary": "self",
      "transportations[\\d].outboundDate": "self",
      "transportations[\\d].returnCompany": "self",
      "transportations[\\d].returnItinerary": "self",
      "transportations[\\d].returnDate": "self",
      ...optionsValidationSchema.value.anchors
    }
  }
});

onMounted(() => {
  validationSchema.value = mergedSchema.value
});

watch(optionsValidationSchema, async () => {
  validationSchema.value = mergedSchema.value
});
</script>
